import { useState } from 'react';

interface Testimonial {
  id: number;
  name: string;
  avatar: string;
  role: string;
  quote: string;
  rating: number;
}

interface Props {
  testimonials: Testimonial[];
}

export default function TestimonialSection({ testimonials }: Props) {
  const [current, setCurrent] = useState(0);
  const t = testimonials[current];
  const total = testimonials.length;

  const goTo = (index: number) => {
    setCurrent((index + total) % total);
  };

  return (
    <section className="py-16 md:py-20 bg-background-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row gap-8 md:gap-12 items-center">
          {/* Left: Avatar image */}
          <div className="w-full lg:w-[40%]">
            <div className="relative w-64 h-64 md:w-80 md:h-80 mx-auto rounded-3xl overflow-hidden bg-accent-100">
              <img
                src={t.avatar}
                alt={t.name}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Right: Testimonial content */}
          <div className="w-full lg:w-[60%] flex flex-col gap-6">
            <span className="text-xs md:text-sm text-foreground-400 tracking-wider">
              （读者好评）
            </span>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight" style={{ fontFamily: "var(--font-heading)" }}>
              万千漫迷的<br />
              <span className="text-foreground-300">真实声音</span>
            </h2>

            {/* Quote */}
            <div className="relative">
              <i className="ri-double-quotes-l text-4xl text-accent-200 absolute -top-3 -left-1"></i>
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed pl-8">
                {t.quote}
              </p>
            </div>

            {/* Author */}
            <div className="flex items-center gap-3">
              <span className="text-foreground-300">—</span>
              <div>
                <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                <p className="text-xs text-foreground-500">{t.role}</p>
              </div>
            </div>

            {/* Stars */}
            <div className="flex gap-1">
              {Array.from({ length: t.rating }).map((_, i) => (
                <i key={i} className="ri-star-fill text-accent-500 text-sm w-4 h-4 flex items-center justify-center"></i>
              ))}
            </div>

            {/* Navigation arrows */}
            <div className="flex gap-3 mt-2">
              <button
                onClick={() => goTo(current - 1)}
                className="w-10 h-10 rounded-full border border-foreground-200 flex items-center justify-center hover:border-foreground-400 transition-colors duration-200 cursor-pointer"
              >
                <i className="ri-arrow-left-line text-foreground-600 text-lg"></i>
              </button>
              <button
                onClick={() => goTo(current + 1)}
                className="w-10 h-10 rounded-full bg-foreground-950 flex items-center justify-center hover:bg-foreground-800 transition-colors duration-200 cursor-pointer"
              >
                <i className="ri-arrow-right-line text-background-50 text-lg"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}